import { useTranslation } from '../../i18n';
import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Input,
  Button,
  Space,
  Row,
  Col,
  InputNumber,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

import TagsInput, { isIpOrCidr, isCountryCode, isAsn, isExtension } from '../TagsInput';
import CountryFlag from '../CountryFlag';

const { Option } = Select;

// Collection types with descriptions
const COLLECTIONS = {
  QS_ARGS: { label: 'Query String', needsVar: true, description: 'Query parameters, e.g., ?name=value' },
  COOKIE: { label: 'Cookie', needsVar: true, description: 'Cookie value' },
  HEADER: { label: 'Header', needsVar: true, description: 'HTTP request headers' },
  URI: { label: 'URI Path', needsVar: false, description: 'Request path, such as /path/to/page' },
  HOST: { label: 'Host', needsVar: false, description: 'Hostname, e.g. example.com' },
  IP: { label: 'Client IP', needsVar: false, description: 'Client IP address' },
  COUNTRY: { label: 'Country Code', needsVar: false, description: 'Country code, e.g., US, CN' },
  ASN: { label: 'ASN', needsVar: false, description: 'ASN (autonomous system number), e.g., 13335' },
  ISP: { label: 'ISP', needsVar: false, description: 'Carrier, e.g. Cloudflare, Inc.' },
  METHOD: { label: 'HTTP Method', needsVar: false, description: 'Request method, such as GET, POST' },
  PROTOCOL: { label: 'HTTP Protocol', needsVar: false, description: 'Request protocol: http or https' },
  PATH: { label: 'Path', needsVar: false, description: 'Path component' },
  EXTENSION: { label: 'File Extension', needsVar: false, description: 'File extension, e.g., jpg, css' },
  IS_HTTPS: { label: 'Is HTTPS', needsVar: false, description: 'Whether the request is HTTPS: equals true / false' },
  USER_AGENT: { label: 'User Agent', needsVar: false, description: 'Browser User-Agent' },
  REFERER: { label: 'Referer', needsVar: false, description: 'HTTP Referer' },
  JA3: { label: 'JA3 (TLS fingerprint)', needsVar: false, description: 'TLS ClientHello JA3 fingerprint (requires TLS frame capture on the node)' },
  JA4: { label: 'JA4 (TLS fingerprint)', needsVar: false, description: 'TLS ClientHello JA4 fingerprint (requires TLS frame capture on the node)' },
  H2: { label: 'HTTP/2 fingerprint', needsVar: false, description: 'HTTP/2 frame fingerprint (requires h2 frame capture on the node)' }
};

// Operators with compatible value types
const OPERATORS = {
  eq: { label: 'Equals', valueType: 'string', description: 'Exact match' },
  neq: { label: 'Does not equal', valueType: 'string', description: 'Does not equal the specified value' },
  contain: { label: 'Contains', valueType: 'string', description: 'Contains substring' },
  not_contain: { label: 'Does not contain', valueType: 'string', description: 'Does not contain substring' },
  starts_with: { label: 'Starts with', valueType: 'string', description: 'Starts with the specified string' },
  ends_with: { label: 'Ends with', valueType: 'string', description: 'Ends with the specified string' },
  glob: { label: 'Matches wildcard', valueType: 'string', description: '* matches any characters, e.g., *.example.com, /api/*' },
  not_glob: { label: 'Does not match wildcard', valueType: 'string', description: 'Does not match wildcard pattern' },
  rx: { label: 'Regex match', valueType: 'string', description: 'Regular expression match' },
  in: { label: 'In list', valueType: 'array', description: 'Value is in the specified list' },
  not_in: { label: 'Not in list', valueType: 'array', description: 'Value is not in the specified list' },
  gt: { label: 'Greater than', valueType: 'number', description: 'Numeric value is greater than' },
  lt: { label: 'Less than', valueType: 'number', description: 'Numeric value is less than' },
  gte: { label: 'Greater than or equal to', valueType: 'number', description: 'Numeric value is greater than or equal to' },
  lte: { label: 'Less than or equal to', valueType: 'number', description: 'Numeric value is less than or equal to' },
  exists: { label: 'Exists', valueType: 'none', description: 'Field exists' },
  not_exists: { label: 'Does not exist', valueType: 'none', description: 'Field does not exist' }
};

// 多值输入:不按空格/逗号拆分的字段(值本身可能含空格)
const FREE_TEXT = ['USER_AGENT', 'REFERER', 'ISP', 'HEADER', 'COOKIE', 'QS_ARGS', 'H2'];
const valueError = (collection, v, t) => {
  switch (collection) {
    case 'IP': return isIpOrCidr(v) ? null : t('Invalid IP / CIDR');
    case 'COUNTRY': return isCountryCode(v) ? null : t('Use a 2-letter country code, e.g. US');
    case 'ASN': return isAsn(v) ? null : t('ASN must be a number');
    case 'METHOD': return /^[A-Z]+$/.test(v) ? null : t('Invalid HTTP method');
    case 'EXTENSION': return isExtension(v) ? null : t('Invalid file extension');
    case 'URI':
    case 'PATH': return v.startsWith('/') ? null : t('Paths start with /');
    default: return null;
  }
};

// Single condition component
const ConditionItem = ({ condition, onChange, onRemove, showLogic = false }) => {
  const { t } = useTranslation();
  const handleCollectionChange = (collectionType) => {
    const needsVar = COLLECTIONS[collectionType]?.needsVar;
    onChange({
      ...condition,
      collection: collectionType,
      collectionVar: needsVar ? (condition.collectionVar || '') : undefined,
      op: condition.op || 'eq',
      var: condition.var || ''
    });
  };

  const handleOperatorChange = (op) => {
    const valueType = OPERATORS[op]?.valueType;
    let newVar = condition.var;

    if (valueType === 'array' && typeof newVar === 'string') {
      newVar = newVar ? [newVar] : [];
    } else if (valueType === 'number') {
      newVar = parseFloat(newVar) || 0;
    } else if (valueType === 'none') {
      newVar = undefined;
    } else if (valueType === 'string' && Array.isArray(newVar)) {
      newVar = newVar.join(',');
    }

    onChange({
      ...condition,
      op,
      var: newVar
    });
  };

  const renderValueInput = () => {
    const valueType = OPERATORS[condition.op]?.valueType;

    if (valueType === 'none') {
      return <Tag color="blue">{t('No value needed')}</Tag>;
    }

    if (valueType === 'number') {
      return (
        <InputNumber
          value={condition.var}
          onChange={(val) => onChange({ ...condition, var: val })}
          placeholder={t('Enter a number')}
          style={{ width: '100%' }}
        />
      );
    }

    if (valueType === 'array') {
      return (
        <TagsInput
          format="array"
          value={Array.isArray(condition.var) ? condition.var : []}
          onChange={(val) => onChange({ ...condition, var: val })}
          placeholder={t('Press Enter after each value')}
          spaceSeparates={!FREE_TEXT.includes(condition.collection)}
          commaSeparates={!FREE_TEXT.includes(condition.collection)}
          uppercase={condition.collection === 'COUNTRY' || condition.collection === 'METHOD'}
          validate={(v) => valueError(condition.collection, v, t)}
          maxTagCount="responsive"
          tagPrefix={condition.collection === 'COUNTRY' ? (v) => <CountryFlag code={v} /> : undefined}
        />
      );
    }

    return (
      <Input
        value={condition.var}
        onChange={(e) => onChange({ ...condition, var: e.target.value })}
        placeholder={condition.op === 'rx' ? t('Enter a regular expression') : t('Enter a value')}
      />
    );
  };

  return (
    <Card size="small" style={{ marginBottom: 8 }}>
      <Row gutter={8} align="middle">
        {showLogic && (
          <Col span={3}>
            <Select
              value={condition.logic || 'and'}
              onChange={(val) => onChange({ ...condition, logic: val })}
              style={{ width: '100%' }}
            >
              <Option value="and">{t('AND')}</Option>
              <Option value="or">{t('Or')}</Option>
            </Select>
          </Col>
        )}

        <Col span={showLogic ? 5 : 6}>
          <Select
            value={condition.collection}
            onChange={handleCollectionChange}
            placeholder={t('Select a data source')}
            style={{ width: '100%' }}
            showSearch
          >
            {Object.entries(COLLECTIONS).map(([key, info]) => (
              <Option key={key} value={key}>
                <Tooltip title={t(info.description)}>{t(info.label)}</Tooltip>
              </Option>
            ))}
          </Select>
        </Col>

        {COLLECTIONS[condition.collection]?.needsVar && (
          <Col span={4}>
            <Input
              value={condition.collectionVar}
              onChange={(e) => onChange({ ...condition, collectionVar: e.target.value })}
              placeholder={t('Parameter name')}
            />
          </Col>
        )}

        <Col span={COLLECTIONS[condition.collection]?.needsVar ? 4 : 5}>
          <Select
            value={condition.op}
            onChange={handleOperatorChange}
            placeholder={t('Operator')}
            style={{ width: '100%' }}
            showSearch
          >
            {Object.entries(OPERATORS).map(([key, info]) => (
              <Option key={key} value={key}>
                <Tooltip title={t(info.description)}>{t(info.label)}</Tooltip>
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={COLLECTIONS[condition.collection]?.needsVar ? 6 : 7}>
          {renderValueInput()}
        </Col>

        <Col span={2}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        </Col>
      </Row>
    </Card>
  );
};

// Main ConditionBuilder component
const ConditionBuilder = ({ value = {}, onChange }) => {
  const { t } = useTranslation();
  const [rootLogic, setRootLogic] = useState(value?.op || 'and');
  const [conditions, setConditions] = useState([]);

  useEffect(() => {
    if (value?.conditions && Array.isArray(value.conditions)) {
      setConditions(value.conditions.map((c, index) => {
        let collection = c.collection;
        let collectionVar = c.collection_var;

        // Handle old format: {"PATH": {}} or {"HEADER": {"var": "X-Custom-Header"}}
        if (typeof c.collection === 'object' && c.collection !== null) {
          const keys = Object.keys(c.collection);
          if (keys.length > 0) {
            collection = keys[0];
            collectionVar = c.collection[keys[0]]?.var;
          }
        }

        return {
          ...c,
          id: Date.now() + index,
          collection,
          collectionVar
        };
      }));
      setRootLogic(value.op || 'and');
    } else if (Object.keys(value).length === 0) {
      // Initialize with one empty condition
      setConditions([{
        id: Date.now(),
        collection: 'PATH',
        op: 'starts_with',
        var: ''
      }]);
    }
  }, []);

  useEffect(() => {
    // Convert to matcher format
    const matcher = convertToMatcher();
    onChange(matcher);
  }, [conditions, rootLogic]);

  const convertToMatcher = () => {
    if (conditions.length === 0) {
      return { op: 'and', conditions: [] };
    }

    const formattedConditions = conditions.map(c => {
      const condition = {
        op: c.op,
        var: c.var,
        collection: c.collection
      };

      // If collection needs a variable (like HEADER, COOKIE, QS_ARGS), add it
      if (COLLECTIONS[c.collection]?.needsVar && c.collectionVar) {
        condition.collection_var = c.collectionVar;
      }

      return condition;
    });

    return {
      op: rootLogic,
      conditions: formattedConditions
    };
  };

  const handleAddCondition = () => {
    setConditions([
      ...conditions,
      {
        id: Date.now(),
        collection: 'PATH',
        op: 'starts_with',
        var: '',
        logic: 'and'
      }
    ]);
  };

  const handleUpdateCondition = (id, newCondition) => {
    setConditions(conditions.map(c =>
      c.id === id ? { ...newCondition, id } : c
    ));
  };

  const handleRemoveCondition = (id) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter(c => c.id !== id));
    }
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card
          size="small"
          title={
            <Space>
              <span>{t('Match conditions')}</span>
              <Tooltip title={t('Perform the configured action when a request meets the following conditions')}>
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
          extra={
            <Select
              value={rootLogic}
              onChange={setRootLogic}
              style={{ width: 120 }}
            >
              <Option value="and">{t('Match all (AND)')}</Option>
              <Option value="or">{t('Match any (OR)')}</Option>
            </Select>
          }
        >
          {conditions.map((condition, index) => (
            <ConditionItem
              key={condition.id}
              condition={condition}
              onChange={(newCondition) => handleUpdateCondition(condition.id, newCondition)}
              onRemove={() => handleRemoveCondition(condition.id)}
              showLogic={false}
            />
          ))}

          <Button
            type="dashed"
            onClick={handleAddCondition}
            block
            icon={<PlusOutlined />}
          >
            {t('Add condition')}
          </Button>
        </Card>

        <Card size="small" type="inner">
          <pre style={{ margin: 0, fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
            {JSON.stringify(convertToMatcher(), null, 2)}
          </pre>
        </Card>
      </Space>
    </div>
  );
};

export default ConditionBuilder;
